import { concat, fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { ComponentLike } from '@glint/template';
import can from 'ember-can/helpers/can';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import { isEmpty } from 'ember-truth-helpers';

import Component from 'umg-ember-backend/components/record-explorer';
import Folders from 'umg-ember-backend/components/record-explorer/for/asset/folders/index';
import UploadHint from 'umg-ember-backend/components/record-explorer/for/asset/upload-hint/index';
import Toolbar from 'umg-ember-backend/components/record-explorer/toolbar/index';
import AssetPreview from 'umg-ember-backend/components/ui/asset-preview/index';
import Button from 'umg-ember-backend/components/ui/button/index';
import ContentWithSidebar from 'umg-ember-backend/components/ui/content-with-sidebar/index';
import FileUploader from 'umg-ember-backend/components/ui/file-uploader/index';
import Checkbox from 'umg-ember-backend/components/ui/form/field/checkbox/index';
import Pagination from 'umg-ember-backend/components/ui/pagination/index';
import Window from 'umg-ember-backend/components/ui/window/index';
import type AbilitiesService from 'umg-ember-backend/services/abilities';
import type AssetUploaderService from 'umg-ember-backend/services/asset-uploader';
import type DataService from 'umg-ember-backend/services/data';
import type FlashMessagesService from 'umg-ember-backend/services/flash-messages';
import type PermissionService from 'umg-ember-backend/services/permission';
import { getBem } from 'umg-ember-backend/utils/get-bem';

import styles from './styles.module.css';
import type { AssetFolder, SetAssetFolder } from './types';

const bem = getBem(styles);

interface AssetToolbarSignature {
  Args: {
    activeColumns: unknown;
    activeFilters: unknown;
    availableFilters: unknown;
    columns: unknown;
    onAddRecord: (...args: unknown[]) => unknown;
    onAssignRelation?: (records: unknown[]) => void;
    onSearch: (value: string) => void;
    onSetFilter: (key: string, value: unknown) => void;
    parentRecordType?: string;
    recordType: string;
    searchString: unknown;
    selectedRecords: unknown[];
  };
  Blocks: {
    actions: [];
  };
  Element: HTMLDivElement;
}

const AssetToolbar = Toolbar as unknown as ComponentLike<AssetToolbarSignature>;

interface AssetExplorerSignature {
  Args: {
    filter?: Record<string, unknown>;
    filters?: Record<string, unknown>;
    onAssignRelation?: (records: unknown[]) => void;
    onBodyScroll?: (scrollPosition: number) => void;
    options?: {
      allowedFolder?: string;
    };
    parentRecordType?: string;
    records?: unknown[];
    recordType: 'asset';
    relationType?: 'belongsTo' | 'hasMany';
    scrollPosition?: number;
  };
}

interface ReloadTask {
  perform(): unknown;
}

type AssetUploader = AssetUploaderService & {
  loadRecords: ReloadTask | null;
  selectedFolderToUpload: AssetFolder | null;
};

interface SelectedFilter {
  field: 'assetFolderId' | 'cmsCreatorId';
  value: string;
}

class RecordExplorerForAssetComponent extends Component {
  declare args: AssetExplorerSignature['Args'];

  // Services
  @service declare abilities: AbilitiesService;
  @service declare assetUploader: AssetUploader;
  @service declare permission: PermissionService;
  @service declare data: DataService;
  @service declare flashMessages: FlashMessagesService;

  // Defaults
  @tracked isUploadHintVisible = false;

  assetFilters = [
    {
      key: 'cmsState',
      // Overwrite defaults to add broken assets (7)
      options: [1, 2, 3, 5, 6, 7],
      type: 'selectValueLabel',
    },
    {
      key: 'channel',
      options: [16, 4, 7, 5],
      type: 'selectValueLabel',
    },
    {
      key: 'customFilter',
      options: [{ property: 'inVideoCollection', value: 0 }],
      type: 'selectCustom',
    },
    {
      key: 'sidebarFilter',
      options: ['assetFolderId', 'cmsCreatorId'],
      type: 'externalFilter',
    },
  ];

  defaultColumns = ['cmsCreated'];

  tableColumns = [
    {
      property: 'cmsCreated',
    },
  ];

  // Getter and setter
  get selectedFolderId() {
    return this.activeFilters.assetFolderId;
  }

  get allowedFolder() {
    return this.args.options?.allowedFolder;
  }

  get selectedFolder() {
    return this.data.assetFolders.find((folder) => {
      return folder.value === this.selectedFolderId;
    });
  }

  get myAssetsSelected() {
    const currentUserId = this.permission.user.id;
    const filterCreatorId = this.activeFilters.cmsCreatorId;

    return filterCreatorId === currentUserId;
  }

  // Modifiers
  registerLoadRecords = modifier(() => {
    this.assetUploader.loadRecords = this.loadRecords;
    return () => (this.assetUploader.loadRecords = null);
  });

  // Actions
  checkPermission = () => {
    this.assetUploader.selectedFolderToUpload = null;

    if (this.abilities.cannot('create', 'asset')) {
      this.flashMessages.error('record_explorer.for.asset.no_permission');

      return false;
    }

    return true;
  };

  setFolder: SetAssetFolder = (folderId) => {
    let option: SelectedFilter | null = null;

    if (folderId) {
      option = {
        field: 'assetFolderId',
        value: folderId,
      };
    }

    const filter: { label: 'assetFolderId' } = {
      label: 'assetFolderId',
    };

    this.setFolderFilter(filter, option);
  };

  toggleMyAssets = () => {
    const filter: { label: 'cmsCreatorId' } = {
      label: 'cmsCreatorId',
    };
    let selected: SelectedFilter | null = {
      value: this.permission.user.id,
      field: 'cmsCreatorId',
    };

    if (this.myAssetsSelected) {
      selected = null;
    }

    this.setFolderFilter(filter, selected);
  };

  setFolderFilter = (
    filter: { label: 'assetFolderId' | 'cmsCreatorId' },
    selected: SelectedFilter | null,
  ) => {
    const filters = this.activeFilters;

    if (selected) {
      filters[filter.label] = selected.value;
    } else {
      delete filters[filter.label];
    }

    this.setFilter('filters', filters);
  };

  toggleUploadHint = () => {
    this.isUploadHintVisible = !this.isUploadHintVisible;
  };

  get uploadHintId() {
    return `asset-upload-hint-${guidFor(this)}`;
  }

  // Template
  <template>
    <Window
      @isBottombarVisible={{if this.recordsData.length true false}}
      @isLoading={{this.loadRecords.isRunning}}
      @isSeamless={{true}}
      @onBodyScroll={{this.setScrollPosition}}
      @scrollPosition={{this.scrollPosition}}
      {{didInsert this.setOrLoadRecords}}
      {{this.registerLoadRecords}}
    >
      <:title>
        {{t (concat 'models.plurals.' this.recordType)}}
      </:title>

      <:toolbar>
        <AssetToolbar
          @activeColumns={{this.activeColumns}}
          @activeFilters={{this.activeFilters}}
          @availableFilters={{this.assetFilters}}
          @columns={{this.tableColumns}}
          @recordType={{this.recordType}}
          @onAddRecord={{fn
            this.addRecord
            (hash assetFolderId=this.selectedFolderId)
          }}
          @onAssignRelation={{@onAssignRelation}}
          @onSearch={{this.search}}
          @onSetFilter={{this.setFilter}}
          @parentRecordType={{@parentRecordType}}
          @searchString={{this.searchString}}
          @selectedRecords={{this.selectedRecords}}
        >
          <:actions>
            {{#if (can 'create' 'asset')}}
              <Button
                @icon='upload'
                @label={{t 'record_explorer.for.asset.direct_upload'}}
                @disabled={{isEmpty this.selectedFolder}}
                @tooltip={{if
                  (isEmpty this.selectedFolder)
                  (t 'record_explorer.for.asset.select_folder')
                  undefined
                }}
                aria-controls={{this.uploadHintId}}
                aria-expanded={{if this.isUploadHintVisible 'true' 'false'}}
                data-test-direct-upload-toggle
                {{on 'click' this.toggleUploadHint}}
              />
            {{/if}}
          </:actions>
        </AssetToolbar>
      </:toolbar>

      <:body>
        <ContentWithSidebar @sidebarPosition='left'>
          <:content>
            <FileUploader
              @isDisabled={{isEmpty this.selectedFolder}}
              @disabledMessage={{t 'record_explorer.for.asset.select_folder'}}
              @onSuccess={{this.assetUploader.saveFile}}
              @onUploadStart={{fn
                this.assetUploader.addFile
                this.selectedFolder
              }}
              @onDragEnter={{fn
                this.assetUploader.showHint
                this.selectedFolder
              }}
              @onDragLeave={{this.assetUploader.hideHint}}
              @onValidateUpload={{this.checkPermission}}
              @isMultiple={{true}}
            >
              {{#if this.isUploadHintVisible}}
                <div
                  class={{bem 'upload-hint'}}
                  id={{this.uploadHintId}}
                  data-test-direct-uploader
                >
                  <FileUploader
                    @isDisabled={{isEmpty this.selectedFolder}}
                    @disabledMessage={{t
                      'record_explorer.for.asset.select_folder'
                    }}
                    @onSuccess={{this.assetUploader.saveFile}}
                    @onUploadStart={{fn
                      this.assetUploader.addFile
                      this.selectedFolder
                    }}
                    @onValidateUpload={{this.checkPermission}}
                    @isMultiple={{true}}
                    @hideOverlay={{true}}
                  />
                </div>
              {{/if}}
              <div class={{bem 'list'}}>
                {{#each this.recordsData as |asset|}}
                  <AssetPreview
                    @asset={{asset}}
                    @cardLayout='list'
                    @columns={{this.visibleColumns}}
                    @selectedRelationalObjects={{this.selectedRecords}}
                    @selectRelationalObjects={{this.selectRecord}}
                    @recordActions={{hash
                      edit=this.loadRecord
                      duplicate=this.duplicateRecord
                      delete=this.deleteRecord
                    }}
                  />
                {{else}}
                  <div class={{bem 'no-records'}}>
                    {{t 'tables.no-records'}}
                  </div>
                {{/each}}
              </div>
            </FileUploader>
          </:content>

          <:sidebar>
            <div class={{bem 'my-assets'}}>
              <Checkbox
                @isChecked={{this.myAssetsSelected}}
                @onLabel={{t 'record_explorer.for.asset.my_assets'}}
                @onChange={{this.toggleMyAssets}}
              />
            </div>
            <Folders
              @allowedFolder={{this.allowedFolder}}
              @folders={{this.data.assetFoldersGrouped}}
              @setFolder={{this.setFolder}}
              @selectedFolderId={{this.selectedFolderId}}
              @onValidateUpload={{this.checkPermission}}
            />
          </:sidebar>
        </ContentWithSidebar>
        <UploadHint @folder={{this.assetUploader.selectedFolderToUpload}} />
      </:body>

      <:bottombar>
        <Pagination
          {{! @glint-ignore: `records` is provided by the untyped base component }}
          @pagination={{this.records.meta}}
          @setPage={{this.setPage}}
          @setPageLimit={{fn this.setFilter 'pageSize'}}
        />
      </:bottombar>
    </Window>
  </template>
}

export default RecordExplorerForAssetComponent as unknown as ComponentLike<AssetExplorerSignature>;
